import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import { useToast } from '../App';
import LoadingState from '../components/LoadingState';
import ConfirmDialog from '../components/ConfirmDialog';
import Categories from './Categories';
import Tags from './Tags';
import Brands from './Brands';
import Attributes from './Attributes';
import Reviews from './Reviews';
import { useModalManager } from '../hooks/useModalManager';
import useBatchProcessor from '../hooks/useBatchProcessor';
import BatchProgressModal from '../components/BatchProgressModal';
import { isPro } from '../Helpers';
import PriceModal from '../components/PriceModal/PriceModal';


const Products = () => {
    const { showSuccess, showError } = useToast();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const { modalConfig, closeModal, showPremiumModal } = useModalManager();
    const { batchProgress, processBatch, resetProgress, shouldUseBatchProcessing } = useBatchProcessor();

    // Get initial tab from URL or default to 'products'
    const initialTab = searchParams.get('tab') || 'products';

    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [settings, setSettings] = useState({});
    const [showBatchModal, setShowBatchModal] = useState(false);
    const [bulkOperationLoading, setBulkOperationLoading] = useState({
        stockUpdate: false,
        publish: false,
        draft: false,
        trash: false,
        delete: false
    });
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [customRowsPerPage, setCustomRowsPerPage] = useState('');
    const [showCustomInput, setShowCustomInput] = useState(false);
    const [totalCount, setTotalCount] = useState(0);
    const [selected, setSelected] = useState([]);
    const [globalSelected, setGlobalSelected] = useState(new Set()); // Persistent selection across pages
    const [viewDialogOpen, setViewDialogOpen] = useState(false);
    const [viewingProduct, setViewingProduct] = useState(null);
    const [activeTab, setActiveTab] = useState(initialTab);
    const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, action: null, data: null });

    // Function to change tab and update URL
    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setSearchParams({ tab });
    };
    const [stockUpdateDialog, setStockUpdateDialog] = useState({
        isOpen: false,
        quantity: '',
        manageStock: true,
        selectedProducts: []
    });

    // Filters
    const [filters, setFilters] = useState({
        search: '',
        status: '',
        category: '',
        stockStatus: ''
    });
    const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
    
    // Advanced filters
    const [advancedFilters, setAdvancedFilters] = useState({
        sortBy: 'date',
        sortOrder: 'desc',
        productType: '',
        minPrice: '',
        maxPrice: '',
        minStock: '',
        maxStock: '',
        featured: '',
        onSale: ''
    });

    const productStatuses = [
        { value: '', label: 'All Statuses' },
        { value: 'publish', label: 'Published' },
        { value: 'draft', label: 'Draft' },
        { value: 'private', label: 'Private' }
    ];

    const stockStatuses = [
        { value: '', label: 'All Stock Status' },
        { value: 'instock', label: 'In Stock' },
        { value: 'outofstock', label: 'Out of Stock' },
        { value: 'onbackorder', label: 'On Backorder' }
    ];

    useEffect(() => {
        loadSettings();
    }, []);

    useEffect(() => {
        if (activeTab === 'products') {
            loadProducts();
        } else {
            // Reset loading state when switching away from products tab
            setLoading(false);
        }
    }, [page, rowsPerPage, filters, activeTab]);

    const loadSettings = async () => {
        try {
            const response = await api.getSettings();
            if (response.success && response.settings) {
                setSettings(response.settings); // Set the full settings object
                console.log('📋 Products - Settings loaded:', response.settings);
            }
        } catch (err) {
            console.error('Failed to load settings:', err);
        }
    };

    const loadProducts = async () => {
        try {
            setLoading(true);
            let loadedProducts = [];

            if (rowsPerPage === 'all') {
                // For "Show All", fetch all products by making multiple API calls
                let currentPage = 1;
                let hasMore = true;

                while (hasMore) {
                    const params = {
                        page: currentPage,
                        per_page: 100 // Use safe batch size
                    };

                    if (filters.search && filters.search.trim()) {
                        params.search = filters.search.trim();
                    }

                    if (filters.status) {
                        params.status = filters.status;
                    }

                    if (filters.stockStatus) {
                        params.stock_status = filters.stockStatus;
                    }

                    const response = await api.getProducts(params);

                    if (response.success) {
                        const products = response.products || [];
                        loadedProducts = [...loadedProducts, ...products];
                        setTotalCount(response.total || 0);

                        // Check if we have more pages
                        hasMore = products.length === 100 && loadedProducts.length < (response.total || 0);
                        currentPage++;
                    } else {
                        hasMore = false;
                        if (loadedProducts.length === 0) {
                            throw new Error(response.message || 'Failed to load products');
                        }
                    }
                }

                setProducts(loadedProducts);
            } else if (rowsPerPage > 100) {
                // For large page sizes, fetch multiple pages to get the requested amount
                let startPage = Math.floor((page * rowsPerPage) / 100) + 1;
                let endPage = Math.ceil(((page + 1) * rowsPerPage) / 100);
                let totalFetched = 0;

                for (let currentPage = startPage; currentPage <= endPage && totalFetched < rowsPerPage; currentPage++) {
                    const params = {
                        page: currentPage,
                        per_page: 100
                    };

                    if (filters.search && filters.search.trim()) {
                        params.search = filters.search.trim();
                    }

                    if (filters.status) {
                        params.status = filters.status;
                    }

                    if (filters.stockStatus) {
                        params.stock_status = filters.stockStatus;
                    }

                    const response = await api.getProducts(params);

                    if (response.success) {
                        const products = response.products || [];
                        loadedProducts = [...loadedProducts, ...products];
                        setTotalCount(response.total || 0);
                        totalFetched += products.length;

                        if (products.length < 100) break; // No more data
                    } else {
                        if (loadedProducts.length === 0) {
                            throw new Error(response.message || 'Failed to load products');
                        }
                        break;
                    }
                }

                // Slice to get exactly the requested page size
                const startIndex = (page * rowsPerPage) % 100;
                const endIndex = startIndex + rowsPerPage;
                const pageProducts = loadedProducts.slice(startIndex, Math.min(endIndex, loadedProducts.length));

                setProducts(pageProducts);
                loadedProducts = pageProducts; // Update loadedProducts for selection calculation
            } else {
                // Regular pagination for 100 or fewer items
                const params = {
                    page: page + 1,
                    per_page: rowsPerPage
                };

                if (filters.search && filters.search.trim()) {
                    params.search = filters.search.trim();
                }

                if (filters.status) {
                    params.status = filters.status;
                }

                if (filters.stockStatus) {
                    params.stock_status = filters.stockStatus;
                }

                const response = await api.getProducts(params);

                if (response.success) {
                    loadedProducts = response.products || [];
                    setProducts(loadedProducts);
                    setTotalCount(response.total || 0);
                } else {
                    throw new Error(response.message || 'Failed to load products');
                }
            }

            // Update selected state to reflect global selection for current page
            const currentPageProductIds = loadedProducts.map(product => product.id);
            const currentPageSelected = currentPageProductIds.filter(id => globalSelected.has(id));
            setSelected(currentPageSelected);

        } catch (err) {
            showError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
        setPage(0);
    };

    const clearFilters = () => {
        setFilters({
            search: '',
            status: '',
            category: '',
            stockStatus: ''
        });
        setAdvancedFilters({
            sortBy: 'date',
            sortOrder: 'desc',
            productType: '',
            minPrice: '',
            maxPrice: '',
            minStock: '',
            maxStock: '',
            featured: '',
            onSale: ''
        });
        setPage(0);
    };

    const handleAdvancedFilterChange = (field, value) => {
        setAdvancedFilters(prev => ({
            ...prev,
            [field]: value
        }));
        setPage(0);
    };

    // Apply client-side sorting and filtering
    const getFilteredAndSortedProducts = () => {
        let filtered = [...products];

        // Apply advanced filters
        if (advancedFilters.productType) {
            filtered = filtered.filter(product => product.type === advancedFilters.productType);
        }

        if (advancedFilters.minPrice) {
            const minPrice = parseFloat(advancedFilters.minPrice);
            filtered = filtered.filter(product => parseFloat(product.price || product.regular_price || 0) >= minPrice);
        }

        if (advancedFilters.maxPrice) {
            const maxPrice = parseFloat(advancedFilters.maxPrice);
            filtered = filtered.filter(product => parseFloat(product.price || product.regular_price || 0) <= maxPrice);
        }

        if (advancedFilters.minStock) {
            const minStock = parseInt(advancedFilters.minStock);
            filtered = filtered.filter(product => (product.stock_quantity || 0) >= minStock);
        }

        if (advancedFilters.maxStock) {
            const maxStock = parseInt(advancedFilters.maxStock);
            filtered = filtered.filter(product => (product.stock_quantity || 0) <= maxStock);
        }

        if (advancedFilters.featured !== '') {
            const isFeatured = advancedFilters.featured === 'true';
            filtered = filtered.filter(product => product.featured === isFeatured);
        }

        if (advancedFilters.onSale !== '') {
            const isOnSale = advancedFilters.onSale === 'true';
            filtered = filtered.filter(product => product.on_sale === isOnSale);
        }

        // Apply sorting
        filtered.sort((a, b) => {
            let comparison = 0;

            switch (advancedFilters.sortBy) {
                case 'date':
                    comparison = new Date(a.date_created) - new Date(b.date_created);
                    break;
                case 'price':
                    const priceA = parseFloat(a.price || a.regular_price || 0);
                    const priceB = parseFloat(b.price || b.regular_price || 0);
                    comparison = priceA - priceB;
                    break;
                case 'stock':
                    comparison = (a.stock_quantity || 0) - (b.stock_quantity || 0);
                    break;
                case 'name':
                    const nameA = (a.name || '').toLowerCase();
                    const nameB = (b.name || '').toLowerCase();
                    comparison = nameA.localeCompare(nameB);
                    break;
                case 'sku':
                    const skuA = (a.sku || '').toLowerCase();
                    const skuB = (b.sku || '').toLowerCase();
                    comparison = skuA.localeCompare(skuB);
                    break;
                default:
                    comparison = 0;
            }

            return advancedFilters.sortOrder === 'asc' ? comparison : -comparison;
        });

        return filtered;
    };

    const handleSelectAll = (event) => {
        if (event.target.checked) {
            // Add all current page products to global selection
            const currentPageProductIds = products.map(product => product.id);
            const newGlobalSelected = new Set([...globalSelected, ...currentPageProductIds]);
            setGlobalSelected(newGlobalSelected);
            setSelected(currentPageProductIds);
        } else {
            // Remove all current page products from global selection
            const currentPageProductIds = products.map(product => product.id);
            const newGlobalSelected = new Set(globalSelected);
            currentPageProductIds.forEach(id => newGlobalSelected.delete(id));
            setGlobalSelected(newGlobalSelected);
            setSelected([]);
        }
    };

    const handleSelectOne = (productId) => {
        const newGlobalSelected = new Set(globalSelected);
        let newSelected;

        if (globalSelected.has(productId)) {
            newGlobalSelected.delete(productId);
            newSelected = selected.filter(id => id !== productId);
        } else {
            newGlobalSelected.add(productId);
            newSelected = [...selected, productId];
        }

        setGlobalSelected(newGlobalSelected);
        setSelected(newSelected);
    };

    const handleEditProduct = (product) => {
        navigate(`/products/edit/${product.id}`);
    };

    const handleViewProduct = async (product) => {
        try {
            // Fetch full product details including descriptions and gallery
            const response = await api.get(`/products/${product.id}`);
            if (response.success && response.product) {
                setViewingProduct(response.product);
            } else {
                // Fallback to list data if API fails
                setViewingProduct(product);
            }
            setViewDialogOpen(true);
        } catch (error) {
            console.error('Error fetching product details:', error);
            // Fallback to list data
            setViewingProduct(product);
            setViewDialogOpen(true);
        }
    };

    const handleExport = async () => {
        // Check if user has premium access for export CSV feature
        if (!isPro()) {
            showPremiumModal();
            return;
        }

        try {
            const response = await api.exportProducts(filters, 'csv');

            if (response.success && response.download_url) {
                window.open(response.download_url, '_blank');
                showSuccess('Export started successfully');
            }
        } catch (err) {
            showError(err.message);
        }
    };

    const handleBulkStockUpdate = () => {
        console.log('🔍 handleBulkStockUpdate called');

        const totalSelected = Array.from(globalSelected);
        console.log('🔍 Total selected products:', totalSelected.length);

        if (totalSelected.length === 0) {
            showError('Please select products to update');
            return;
        }

        // Get selected products details from all pages
        const selectedProductsData = products.filter(p => globalSelected.has(p.id));

        // Check if any product has stock managed
        const hasStockManaged = selectedProductsData.some(p => p.manage_stock);

        // Get average or first stock quantity as default
        const stockQuantities = selectedProductsData
            .filter(p => p.manage_stock && p.stock_quantity)
            .map(p => p.stock_quantity);
        const avgStock = stockQuantities.length > 0
            ? Math.round(stockQuantities.reduce((a, b) => a + b, 0) / stockQuantities.length)
            : '';

        setStockUpdateDialog({
            isOpen: true,
            quantity: avgStock,
            manageStock: true,
            selectedProducts: selectedProductsData
        });
    };

    const executeStockUpdate = async () => {
        if (!stockUpdateDialog.manageStock) {
            showError('Please enable "Manage Stock" to set stock quantity');
            return;
        }

        const quantity = parseInt(stockUpdateDialog.quantity);
        if (isNaN(quantity) || quantity < 0) {
            showError('Please enter a valid stock quantity');
            return;
        }

        try {
            const totalSelected = Array.from(globalSelected);
            const shouldUseBatch = shouldUseBatchProcessing(totalSelected.length, settings);

            console.log('🔍 Bulk Stock Update - Processing Decision:', {
                productCount: totalSelected.length,
                settings: settings,
                shouldUseBatch: shouldUseBatch,
                batchSize: parseInt(settings.bulk_edit_batch_size) || 5000,
                threshold: parseInt(settings.bulk_edit_threshold) || 1000
            });

            if (shouldUseBatch) {
                // Use batch processing for large datasets
                setShowBatchModal(true);

                // Prepare stock updates in the format backend expects
                const stockUpdates = totalSelected.map(productId => ({
                    product_id: productId,
                    quantity: quantity
                }));

                await processBatch(
                    totalSelected,
                    'stockUpdate',
                    { stockData: stockUpdates },
                    parseInt(settings.bulk_edit_batch_size) || 5000,
                    'products'
                );

                showSuccess(`Successfully updated stock for ${totalSelected.length} products using batch processing`);
            } else {
                // Use regular processing for smaller datasets - show loading state
                setBulkOperationLoading(prev => ({ ...prev, stockUpdate: true }));

                const stockUpdates = totalSelected.map(productId => ({
                    product_id: productId,
                    quantity: quantity
                }));

                const response = await api.bulkUpdateProductStock(stockUpdates);

                if (response.success) {
                    showSuccess(`Stock updated for ${totalSelected.length} products`);
                } else {
                    throw new Error(response.message || 'Failed to update stock');
                }
            }

            await loadProducts();
            setSelected([]);
            setGlobalSelected(new Set()); // Clear global selection
            setStockUpdateDialog({
                isOpen: false,
                quantity: '',
                manageStock: true,
                selectedProducts: []
            });
        } catch (err) {
            showError(err.message);
        } finally {
            setBulkOperationLoading(prev => ({ ...prev, stockUpdate: false }));
            setShowBatchModal(false);
        }
    };

    const handleBulkStatusUpdate = async (status) => {
        console.log('🔍 handleBulkStatusUpdate called with status:', status);

        const totalSelected = Array.from(globalSelected);
        console.log('🔍 Total selected products:', totalSelected.length);

        if (totalSelected.length === 0) {
            showError('Please select products to update');
            return;
        }

        const statusLabel = status === 'publish' ? 'published' : 'draft';
        console.log('🔍 Setting confirmation dialog for status update:', { status, statusLabel });

        setConfirmDialog({
            isOpen: true,
            action: 'bulkStatus',
            data: { status, statusLabel }
        });
    };

    const handleBulkDelete = async (force = false) => {
        const totalSelected = Array.from(globalSelected);
        if (totalSelected.length === 0) {
            showError('Please select products to delete');
            return;
        }

        const action = force ? 'permanently delete' : 'move to trash';
        setConfirmDialog({
            isOpen: true,
            action: 'bulkDelete',
            data: { force, action }
        });
    };

    const executeConfirmedAction = async () => {
        const { action, data } = confirmDialog;
        const totalSelected = Array.from(globalSelected);

        console.log('🔍 executeConfirmedAction called:', { action, data, totalSelected: totalSelected.length });

        try {
            const shouldUseBatch = shouldUseBatchProcessing(totalSelected.length, settings);

            console.log('🔍 Bulk Product Operation - Processing Decision:', {
                productCount: totalSelected.length,
                action: action,
                settings: settings,
                shouldUseBatch: shouldUseBatch,
                batchSize: parseInt(settings.bulk_edit_batch_size) || 5000,
                threshold: parseInt(settings.bulk_edit_threshold) || 1000
            });

            if (shouldUseBatch) {
                console.log('🚀 Using batch processing for products');
                // Use batch processing for large datasets
                setShowBatchModal(true);

                if (action === 'bulkStatus') {
                    console.log('🔄 Starting batch status update for products');
                    await processBatch(
                        totalSelected,
                        'statusUpdate',
                        { status: data.status },
                        parseInt(settings.bulk_edit_batch_size) || 5000,
                        'products'
                    );
                    showSuccess(`Successfully updated ${totalSelected.length} products to ${data.statusLabel} using batch processing`);
                } else if (action === 'bulkDelete') {
                    console.log('🔄 Starting batch delete for products');
                    await processBatch(
                        totalSelected,
                        'delete',
                        { force: data.force },
                        parseInt(settings.bulk_edit_batch_size) || 5000,
                        'products'
                    );
                    showSuccess(`Successfully ${data.force ? 'deleted' : 'moved to trash'} ${totalSelected.length} products using batch processing`);
                }
            } else {
                console.log('📦 Using regular processing for products');
                // Use regular processing for smaller datasets - show loading states
                if (action === 'bulkStatus') {
                    // Set loading state based on status
                    const loadingKey = data.status === 'publish' ? 'publish' : 'draft';
                    setBulkOperationLoading(prev => ({ ...prev, [loadingKey]: true }));

                    console.log('📡 Calling regular bulkUpdateProductStatus');
                    const response = await api.bulkUpdateProductStatus(totalSelected, data.status);
                    if (response.success) {
                        showSuccess(`${totalSelected.length} products set to ${data.statusLabel}`);
                    } else {
                        throw new Error(response.message || 'Failed to update status');
                    }
                } else if (action === 'bulkDelete') {
                    // Set loading state based on delete type
                    const loadingKey = data.force ? 'delete' : 'trash';
                    setBulkOperationLoading(prev => ({ ...prev, [loadingKey]: true }));

                    console.log('📡 Calling regular bulkDeleteProducts');
                    const response = await api.bulkDeleteProducts(totalSelected, data.force);
                    if (response.success) {
                        showSuccess(`${totalSelected.length} products ${data.force ? 'deleted permanently' : 'moved to trash'}`);
                    } else {
                        throw new Error(response.message || `Failed to ${data.action} products`);
                    }
                }
            }

            await loadProducts();
            setSelected([]);
            setGlobalSelected(new Set()); // Clear global selection

        } catch (err) {
            console.error('❌ Product bulk operation failed:', err);
            showError(err.message);
        } finally {
            // Reset all loading states
            setBulkOperationLoading({
                stockUpdate: false,
                publish: false,
                draft: false,
                trash: false,
                delete: false
            });
            setShowBatchModal(false);
        }
    };

    const getStockStatusChipClass = (stockStatus) => {
        const statusClasses = {
            instock: 'chip-success',
            outofstock: 'chip-danger',
            onbackorder: 'chip-warning'
        };
        return statusClasses[stockStatus] || 'chip-secondary';
    };

    const formatCurrency = (amount, currency = 'USD') => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency
        }).format(amount);
    };

    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    const handleRowsPerPageChange = (event) => {
        const value = event.target.value;
        if (value === 'custom') {
            setShowCustomInput(true);
            return;
        }

        // Reset page to 0 when changing page size
        setPage(0);
        setRowsPerPage(value === 'all' ? 'all' : parseInt(value, 10));
        setShowCustomInput(false);
    };

    const handleCustomRowsPerPageSubmit = () => {
        const customValue = parseInt(customRowsPerPage, 10);
        if (customValue && customValue > 0 && customValue <= 10000) {
            setRowsPerPage(customValue);
            setPage(0);
            setShowCustomInput(false);
            setCustomRowsPerPage('');
        } else {
            showError('Please enter a valid number between 1 and 10,000');
        }
    };

    const handleCustomRowsPerPageCancel = () => {
        setShowCustomInput(false);
        setCustomRowsPerPage('');
    };

    // Only show full-page loader on initial load of products tab
    const isInitialLoad = activeTab === 'products' && loading && products.length === 0 && !filters.search && !filters.status;

    if (isInitialLoad) {
        return (
            <div className="page">
                <div className="container container-xl">
                    <LoadingState message="Loading products..." />
                </div>
            </div>
        );
    }

    return (
        <div className="page products">
            <div className="container product-container container-xl">
                {/* Header */}
                <div className="page-header">
                    <div className="page-title">
                        <h1>Products Management</h1>
                        <p>Manage WooCommerce products with inventory and stock synchronization</p>
                    </div>
                </div>



                {/* Main Layout with Sidebar */}
                <div className="products-layout">
                    {/* Sidebar */}
                    <div className="products-sidebar">
                        <div className="sidebar-tabs">
                            <button
                                className={`sidebar-tab ${activeTab === 'products' ? 'active' : ''}`}
                                onClick={() => handleTabChange('products')}
                            >
                                <span className="tab-icon">📦</span>
                                <span className="tab-label">Products List</span>
                            </button>
                            <button
                                className={`sidebar-tab ${activeTab === 'categories' ? 'active' : ''}`}
                                onClick={() => handleTabChange('categories')}
                            >
                                <span className="tab-icon">🏷️</span>
                                <span className="tab-label">Categories</span>
                            </button>
                            <button
                                className={`sidebar-tab ${activeTab === 'tags' ? 'active' : ''}`}
                                onClick={() => handleTabChange('tags')}
                            >
                                <span className="tab-icon">🔖</span>
                                <span className="tab-label">Tags</span>
                            </button>
                            <button
                                className={`sidebar-tab ${activeTab === 'brands' ? 'active' : ''}`}
                                onClick={() => handleTabChange('brands')}
                            >
                                <span className="tab-icon">🎨</span>
                                <span className="tab-label">Brands</span>
                            </button>
                            <button
                                className={`sidebar-tab ${activeTab === 'attributes' ? 'active' : ''}`}
                                onClick={() => handleTabChange('attributes')}
                            >
                                <span className="tab-icon">⚙️</span>
                                <span className="tab-label">Attributes</span>
                            </button>
                            <button
                                className={`sidebar-tab ${activeTab === 'reviews' ? 'active' : ''}`}
                                onClick={() => handleTabChange('reviews')}
                            >
                                <span className="tab-icon">⭐</span>
                                <span className="tab-label">Reviews</span>
                            </button>
                        </div>
                    </div>

                    {/* Main Content */}
                    <div className="products-main-content">
                        {activeTab === 'products' && (
                            <div className="products-container">
                                <div className="page-header">
                                    <div className="page-actions">
                                        <button
                                            className="btn btn-success btn-sm"
                                            onClick={() => navigate('/products/add')}
                                        >
                                            +  Create Product
                                        </button>
                                        {settings.sync_products && (
                                            <button
                                                className={`btn btn-secondary btn-sm ${!isPro() ? 'premium-disabled' : ''}`}
                                                onClick={handleExport}
                                                title={!isPro() ? 'Premium Feature - Upgrade to unlock' : 'Export products to CSV'}
                                            >
                                                📥 Export CSV {!isPro() ? '🔒' : ''}
                                            </button>
                                        )}

                                        <button
                                            className="btn btn-secondary btn-sm"
                                            onClick={loadProducts}
                                            disabled={loading}
                                        >
                                            🔄 Refresh
                                        </button>
                                    </div>
                                </div>

                                {/* Filters Bar */}
                                <div className="filters-bar">
                                    <div className="filters-row">
                                        <div className="filter-item filter-search">
                                            <input
                                                type="text"
                                                placeholder="🔍 Search products..."
                                                value={filters.search}
                                                onChange={(e) => handleFilterChange('search', e.target.value)}
                                                className="search-input"
                                            />
                                        </div>
                                        <div className="filter-item">
                                            <select
                                                value={filters.status}
                                                onChange={(e) => handleFilterChange('status', e.target.value)}
                                                className="filter-select"
                                            >
                                                {productStatuses.map(status => (
                                                    <option key={status.value} value={status.value}>
                                                        {status.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="filter-item">
                                            <select
                                                value={filters.stockStatus}
                                                onChange={(e) => handleFilterChange('stockStatus', e.target.value)}
                                                className="filter-select"
                                            >
                                                {stockStatuses.map(status => (
                                                    <option key={status.value} value={status.value}>
                                                        {status.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="filter-item">
                                            <button
                                                className={`btn btn-secondary btn-sm ${showAdvancedFilters ? 'active' : ''}`}
                                                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                                                title="Show more filters"
                                            >
                                                {showAdvancedFilters ? '▲ Less' : '▼ More'}
                                            </button>
                                        </div>
                                        <div className="filter-item">
                                            <button
                                                className="btn btn-secondary btn-clear"
                                                onClick={clearFilters}
                                                title="Clear all filters"
                                            >
                                                ✕ Clear
                                            </button>
                                        </div>
                                    </div>

                                    {/* Advanced Filters - Expandable */}
                                    <div className={`advanced-filters ${showAdvancedFilters ? 'expanded' : ''} ${!isPro() ? 'premium-locked' : ''}`}>
                                        {!isPro() && showAdvancedFilters && (
                                            <div className="premium-overlay" onClick={showPremiumModal}>
                                                <div className="premium-badge">
                                                    Unlock Advanced Filters
                                                </div>
                                            </div>
                                        )}
                                        <div className="advanced-filters-content">
                                            {/* Sorting Options */}
                                            <div className="filter-section">
                                                <h4 className="filter-section-title">📊 Sort & Order</h4>
                                                <div className="filter-group">
                                                    <div className="filter-item">
                                                        <label>Sort By</label>
                                                        <select
                                                            value={advancedFilters.sortBy}
                                                            onChange={(e) => handleAdvancedFilterChange('sortBy', e.target.value)}
                                                            className="filter-select"
                                                        >
                                                            <option value="date">Date Created</option>
                                                            <option value="name">Product Name</option>
                                                            <option value="price">Price</option>
                                                            <option value="stock">Stock Quantity</option>
                                                            <option value="sku">SKU</option>
                                                        </select>
                                                    </div>
                                                    <div className="filter-item">
                                                        <label>Order</label>
                                                        <select
                                                            value={advancedFilters.sortOrder}
                                                            onChange={(e) => handleAdvancedFilterChange('sortOrder', e.target.value)}
                                                            className="filter-select"
                                                        >
                                                            <option value="asc">Ascending ↑</option>
                                                            <option value="desc">Descending ↓</option>
                                                        </select>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Product Type Filter */}
                                            <div className="filter-section">
                                                <h4 className="filter-section-title">📦 Product Type</h4>
                                                <div className="filter-group">
                                                    <div className="filter-item">
                                                        <label>Type</label>
                                                        <select
                                                            value={advancedFilters.productType}
                                                            onChange={(e) => handleAdvancedFilterChange('productType', e.target.value)}
                                                            className="filter-select"
                                                        >
                                                            <option value="">All Types</option>
                                                            <option value="simple">Simple</option>
                                                            <option value="variable">Variable</option>
                                                            <option value="grouped">Grouped</option>
                                                            <option value="external">External/Affiliate</option>
                                                        </select>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Price Range Filter */}
                                            <div className="filter-section">
                                                <h4 className="filter-section-title">💰 Price Range</h4>
                                                <div className="filter-group">
                                                    <div className="filter-item">
                                                        <label>Min Price</label>
                                                        <input
                                                            type="number"
                                                            placeholder="0.00"
                                                            value={advancedFilters.minPrice}
                                                            onChange={(e) => handleAdvancedFilterChange('minPrice', e.target.value)}
                                                            className="filter-input"
                                                            min="0"
                                                            step="0.01"
                                                        />
                                                    </div>
                                                    <div className="filter-item">
                                                        <label>Max Price</label>
                                                        <input
                                                            type="number"
                                                            placeholder="999999.99"
                                                            value={advancedFilters.maxPrice}
                                                            onChange={(e) => handleAdvancedFilterChange('maxPrice', e.target.value)}
                                                            className="filter-input"
                                                            min="0"
                                                            step="0.01"
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Stock Quantity Filter */}
                                            <div className="filter-section">
                                                <h4 className="filter-section-title">📊 Stock Quantity</h4>
                                                <div className="filter-group">
                                                    <div className="filter-item">
                                                        <label>Min Stock</label>
                                                        <input
                                                            type="number"
                                                            placeholder="0"
                                                            value={advancedFilters.minStock}
                                                            onChange={(e) => handleAdvancedFilterChange('minStock', e.target.value)}
                                                            className="filter-input"
                                                            min="0"
                                                            step="1"
                                                        />
                                                    </div>
                                                    <div className="filter-item">
                                                        <label>Max Stock</label>
                                                        <input
                                                            type="number"
                                                            placeholder="9999"
                                                            value={advancedFilters.maxStock}
                                                            onChange={(e) => handleAdvancedFilterChange('maxStock', e.target.value)}
                                                            className="filter-input"
                                                            min="0"
                                                            step="1"
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Product Attributes */}
                                            <div className="filter-section">
                                                <h4 className="filter-section-title">⭐ Product Attributes</h4>
                                                <div className="filter-group">
                                                    <div className="filter-item">
                                                        <label>Featured</label>
                                                        <select
                                                            value={advancedFilters.featured}
                                                            onChange={(e) => handleAdvancedFilterChange('featured', e.target.value)}
                                                            className="filter-select"
                                                        >
                                                            <option value="">All Products</option>
                                                            <option value="true">Featured Only</option>
                                                            <option value="false">Not Featured</option>
                                                        </select>
                                                    </div>
                                                    <div className="filter-item">
                                                        <label>On Sale</label>
                                                        <select
                                                            value={advancedFilters.onSale}
                                                            onChange={(e) => handleAdvancedFilterChange('onSale', e.target.value)}
                                                            className="filter-select"
                                                        >
                                                            <option value="">All Products</option>
                                                            <option value="true">On Sale Only</option>
                                                            <option value="false">Not On Sale</option>
                                                        </select>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Bulk Actions */}
                                {globalSelected.size > 0 && (
                                    <div className="card mb-4">
                                        <div className="card-content">
                                            <div className="bulk-actions flex items-center justify-between">
                                                <div className="bulk-info">
                                                    <strong>{globalSelected.size} products selected</strong>
                                                    {globalSelected.size !== selected.length && (
                                                        <span className="text-gray-600 ml-2">
                                                            ({selected.length} on current page)
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="bulk-buttons flex gap-2">
                                                    <button
                                                        className={`btn btn-primary btn-sm ${(!isPro() && globalSelected.size > 100) ? 'premium-disabled' : ''}`}
                                                        onClick={() => {
                                                            if (!isPro() && globalSelected.size > 100) {
                                                                showPremiumModal();
                                                                return;
                                                            }
                                                            navigate('/products/bulk-edit', { state: { selectedProducts: Array.from(globalSelected) } });
                                                        }}
                                                        title={(!isPro() && globalSelected.size > 100) ? 'Premium Feature - Bulk edit is limited to 100 items' : 'Bulk edit selected products'}
                                                    >
                                                        ✏️ Bulk Edit {(!isPro() && globalSelected.size > 100) ? '🔒' : ''}
                                                    </button>
                                                    <button
                                                        className={`btn btn-primary btn-sm ${bulkOperationLoading.stockUpdate ? 'btn-loading' : ''}`}
                                                        onClick={() => handleBulkStockUpdate()}
                                                        disabled={bulkOperationLoading.stockUpdate}
                                                    >
                                                        {bulkOperationLoading.stockUpdate ? (
                                                            <>
                                                                <div className="spinner"></div>
                                                                Updating...
                                                            </>
                                                        ) : (
                                                            'Update Stock'
                                                        )}
                                                    </button>
                                                    <button
                                                        className={`btn btn-success btn-sm ${bulkOperationLoading.publish ? 'btn-loading' : ''}`}
                                                        onClick={() => {
                                                            console.log('🔍 Publish button clicked');
                                                            handleBulkStatusUpdate('publish');
                                                        }}
                                                        disabled={bulkOperationLoading.publish}
                                                    >
                                                        {bulkOperationLoading.publish ? (
                                                            <>
                                                                <div className="spinner"></div>
                                                                Publishing...
                                                            </>
                                                        ) : (
                                                            'Publish'
                                                        )}
                                                    </button>
                                                    <button
                                                        className={`btn btn-warning btn-sm ${bulkOperationLoading.draft ? 'btn-loading' : ''}`}
                                                        onClick={() => {
                                                            console.log('🔍 Set to Draft button clicked');
                                                            handleBulkStatusUpdate('draft');
                                                        }}
                                                        disabled={bulkOperationLoading.draft}
                                                    >
                                                        {bulkOperationLoading.draft ? (
                                                            <>
                                                                <div className="spinner"></div>
                                                                Processing...
                                                            </>
                                                        ) : (
                                                            'Set to Draft'
                                                        )}
                                                    </button>
                                                    <button
                                                        className={`btn btn-warning btn-sm ${bulkOperationLoading.trash ? 'btn-loading' : ''}`}
                                                        onClick={() => handleBulkDelete(false)}
                                                        disabled={bulkOperationLoading.trash}
                                                    >
                                                        {bulkOperationLoading.trash ? (
                                                            <>
                                                                <div className="spinner"></div>
                                                                Moving...
                                                            </>
                                                        ) : (
                                                            'Move to Trash'
                                                        )}
                                                    </button>
                                                    <button
                                                        className={`btn btn-danger btn-sm ${bulkOperationLoading.delete ? 'btn-loading' : ''}`}
                                                        onClick={() => handleBulkDelete(true)}
                                                        disabled={bulkOperationLoading.delete}
                                                    >
                                                        {bulkOperationLoading.delete ? (
                                                            <>
                                                                <div className="spinner"></div>
                                                                Deleting...
                                                            </>
                                                        ) : (
                                                            'Delete Permanently'
                                                        )}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Products Table */}
                                <div className="card">
                                    <div className="table-container">
                                        <table className="products-table">
                                            <thead>
                                                <tr>
                                                    <th className="checkbox-col">
                                                        <input
                                                            type="checkbox"
                                                            checked={products.length > 0 && selected.length === products.length}
                                                            onChange={handleSelectAll}
                                                        />
                                                    </th>
                                                    <th>Product</th>
                                                    <th>SKU</th>
                                                    <th>Stock</th>
                                                    <th>Price</th>
                                                    <th>Status</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {loading ? (
                                                    <tr>
                                                        <td colSpan="7" className="text-center">
                                                            <LoadingState message={
                                                                rowsPerPage === 'all' ? "Loading all products..." :
                                                                    rowsPerPage > 100 ? `Loading ${rowsPerPage} products...` :
                                                                        "Loading products..."
                                                            } />
                                                        </td>
                                                    </tr>
                                                ) : products.length === 0 ? (
                                                    <tr>
                                                        <td colSpan="7" className="text-center">
                                                            <div className="empty-state">
                                                                <div className="empty-icon">📦</div>
                                                                <p>No products found</p>
                                                                <p className="text-gray-600">Try adjusting your filters or sync data</p>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    getFilteredAndSortedProducts().map((product) => (
                                                        <tr key={product.id} className="product-row">
                                                            <td className="checkbox-col">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={globalSelected.has(product.id)}
                                                                    onChange={() => handleSelectOne(product.id)}
                                                                />
                                                            </td>
                                                            <td>
                                                                <div className="product-info">
                                                                    <div className="product-image">
                                                                        {product.image ? (
                                                                            <img src={product.image} alt={product.name} />
                                                                        ) : (
                                                                            <div className="no-image">📦</div>
                                                                        )}
                                                                    </div>
                                                                    <div className="product-details">
                                                                        <div className="product-name">
                                                                            {product.name}
                                                                        </div>
                                                                        <div className="product-type text-gray-600">
                                                                            {product.type || 'Simple'}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <div className="product-sku">
                                                                    {product.sku || 'N/A'}
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <div className="stock-info">
                                                                    <div className={`chip ${getStockStatusChipClass(product.stock_status)}`}>
                                                                        {product.stock_status}
                                                                    </div>
                                                                    {product.manage_stock && (
                                                                        <div className="stock-quantity text-gray-600">
                                                                            Qty: {product.stock_quantity || 0}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <div className="product-price font-semibold">
                                                                    {product.price ? formatCurrency(product.price) : 'N/A'}
                                                                </div>
                                                                {product.sale_price && (
                                                                    <div className="sale-price text-gray-600">
                                                                        Sale: {formatCurrency(product.sale_price)}
                                                                    </div>
                                                                )}
                                                            </td>
                                                            <td>
                                                                <div className={`chip ${product.status === 'publish' ? 'chip-success' : 'chip-warning'}`}>
                                                                    {product.status}
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <div className="product-actions flex gap-1">
                                                                    <button
                                                                        className="btn btn-secondary btn-sm"
                                                                        onClick={() => handleEditProduct(product)}
                                                                        title="Edit Product"
                                                                    >
                                                                        ✏️
                                                                    </button>
                                                                    <button
                                                                        className="btn btn-secondary btn-sm"
                                                                        onClick={() => handleViewProduct(product)}
                                                                        title="View Product"
                                                                    >
                                                                        👁️
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Pagination */}
                                    {totalCount > 0 && (
                                        <div className="table-pagination">
                                            <div className="pagination-info">
                                                {rowsPerPage === 'all' ? (
                                                    `Showing all ${totalCount} products`
                                                ) : (
                                                    `Showing ${page * rowsPerPage + 1} to ${Math.min((page + 1) * rowsPerPage, totalCount)} of ${totalCount} products`
                                                )}
                                                {globalSelected.size > 0 && (
                                                    <span className="selected-info"> • {globalSelected.size} selected</span>
                                                )}
                                            </div>
                                            <div className="pagination-controls flex items-center gap-2">
                                                <select
                                                    value={showCustomInput ? 'custom' : rowsPerPage}
                                                    onChange={handleRowsPerPageChange}
                                                    className="rows-per-page"
                                                >
                                                    <option value={10}>10 per page</option>
                                                    <option value={25}>25 per page</option>
                                                    <option value={50}>50 per page</option>
                                                    <option value={100}>100 per page</option>
                                                    <option value={200}>200 per page</option>
                                                    <option value={500}>500 per page</option>
                                                    <option value={1000}>1000 per page</option>
                                                    <option value="all">Show All</option>
                                                    <option value="custom">Custom...</option>
                                                </select>

                                                {showCustomInput && (
                                                    <div className="custom-rows-input">
                                                        <input
                                                            type="number"
                                                            value={customRowsPerPage}
                                                            onChange={(e) => setCustomRowsPerPage(e.target.value)}
                                                            placeholder="Enter number (1-10000)"
                                                            min="1"
                                                            max="10000"
                                                            className="custom-input"
                                                        />
                                                        <button
                                                            onClick={handleCustomRowsPerPageSubmit}
                                                            className="btn btn-primary btn-xs"
                                                        >
                                                            Apply
                                                        </button>
                                                        <button
                                                            onClick={handleCustomRowsPerPageCancel}
                                                            className="btn btn-secondary btn-xs"
                                                        >
                                                            Cancel
                                                        </button>
                                                    </div>
                                                )}

                                                {rowsPerPage !== 'all' && (
                                                    <>
                                                        <button
                                                            className="btn btn-secondary btn-sm"
                                                            onClick={() => handlePageChange(page - 1)}
                                                            disabled={page === 0}
                                                        >
                                                            ← Previous
                                                        </button>
                                                        <span className="page-info">
                                                            Page {page + 1} of {Math.ceil(totalCount / rowsPerPage)}
                                                        </span>
                                                        <button
                                                            className="btn btn-secondary btn-sm"
                                                            onClick={() => handlePageChange(page + 1)}
                                                            disabled={(page + 1) * rowsPerPage >= totalCount}
                                                        >
                                                            Next →
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {activeTab === 'categories' && (
                            <Categories />
                        )}

                        {activeTab === 'tags' && (
                            <Tags />
                        )}

                        {activeTab === 'brands' && (
                            <Brands />
                        )}

                        {activeTab === 'attributes' && (
                            <Attributes />
                        )}

                        {activeTab === 'reviews' && (
                            <Reviews />
                        )}
                    </div>
                </div>

                {/* View Product Modal */}
                {viewDialogOpen && (
                    <ProductViewModal
                        product={viewingProduct}
                        onClose={() => {
                            setViewDialogOpen(false);
                            setViewingProduct(null);
                        }}
                    />
                )}

                {/* Confirmation Dialog */}
                <ConfirmDialog
                    isOpen={confirmDialog.isOpen}
                    onClose={() => setConfirmDialog({ isOpen: false, action: null, data: null })}
                    onConfirm={executeConfirmedAction}
                    title={confirmDialog.action === 'bulkStatus' ? 'Confirm Status Update' : 'Confirm Delete'}
                    confirmText={confirmDialog.action === 'bulkStatus' ? 'Update Status' : 'Delete'}
                    confirmButtonClass={confirmDialog.action === 'bulkDelete' ? 'btn-danger' : 'btn-primary'}
                >
                    {confirmDialog.action === 'bulkStatus' && (
                        <p>Are you sure you want to set <strong>{Array.from(globalSelected).length} products</strong> to <strong>{confirmDialog.data?.statusLabel}</strong>?</p>
                    )}
                    {confirmDialog.action === 'bulkDelete' && (
                        <>
                            <p>Are you sure you want to <strong>{confirmDialog.data?.action}</strong> <strong>{Array.from(globalSelected).length} products</strong>?</p>
                            {confirmDialog.data?.force && <p className="text-danger">This action cannot be undone.</p>}
                        </>
                    )}
                </ConfirmDialog>

                {/* Stock Update Dialog */}
                <ConfirmDialog
                    isOpen={stockUpdateDialog.isOpen}
                    onClose={() => setStockUpdateDialog({
                        isOpen: false,
                        quantity: '',
                        manageStock: true,
                        selectedProducts: []
                    })}
                    onConfirm={executeStockUpdate}
                    title="Update Stock Quantity"
                    confirmText="Update Stock"
                    confirmButtonClass="btn-primary"
                >
                    <div style={{ marginBottom: '16px' }}>
                        <p style={{ marginBottom: '12px' }}>
                            Updating stock for <strong>{Array.from(globalSelected).length} selected products</strong>
                        </p>

                        {/* Show current stock info */}
                        {stockUpdateDialog.selectedProducts.length > 0 && (
                            <div style={{
                                background: '#f7fafc',
                                padding: '12px',
                                borderRadius: '6px',
                                marginBottom: '16px',
                                fontSize: '13px'
                            }}>
                                <div style={{ marginBottom: '8px', fontWeight: '600', color: '#2d3748' }}>
                                    Current Stock Status:
                                </div>
                                {stockUpdateDialog.selectedProducts.slice(0, 3).map(product => (
                                    <div key={product.id} style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        padding: '4px 0',
                                        color: '#4a5568'
                                    }}>
                                        <span>{product.name}</span>
                                        <span style={{ fontWeight: '500' }}>
                                            {product.manage_stock
                                                ? `${product.stock_quantity || 0} units`
                                                : 'Not managed'}
                                        </span>
                                    </div>
                                ))}
                                {stockUpdateDialog.selectedProducts.length > 3 && (
                                    <div style={{ color: '#718096', fontSize: '12px', marginTop: '4px' }}>
                                        +{stockUpdateDialog.selectedProducts.length - 3} more products
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Manage Stock Checkbox */}
                        <div style={{ marginBottom: '12px' }}>
                            <label style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                cursor: 'pointer',
                                fontSize: '14px'
                            }}>
                                <input
                                    type="checkbox"
                                    checked={stockUpdateDialog.manageStock}
                                    onChange={(e) => setStockUpdateDialog({
                                        ...stockUpdateDialog,
                                        manageStock: e.target.checked
                                    })}
                                    style={{ cursor: 'pointer' }}
                                />
                                <span>Enable stock management for selected products</span>
                            </label>
                        </div>

                        {/* Stock Quantity Input */}
                        <div>
                            <label style={{
                                display: 'block',
                                marginBottom: '6px',
                                fontSize: '14px',
                                fontWeight: '500',
                                color: '#2d3748'
                            }}>
                                New Stock Quantity:
                            </label>
                            <input
                                type="number"
                                min="0"
                                className="form-control"
                                placeholder="Enter quantity"
                                value={stockUpdateDialog.quantity}
                                onChange={(e) => setStockUpdateDialog({
                                    ...stockUpdateDialog,
                                    quantity: e.target.value
                                })}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter' && stockUpdateDialog.manageStock) {
                                        executeStockUpdate();
                                    }
                                }}
                                disabled={!stockUpdateDialog.manageStock}
                                autoFocus
                                style={{ width: '100%' }}
                            />
                            {!stockUpdateDialog.manageStock && (
                                <p style={{
                                    fontSize: '12px',
                                    color: '#e53e3e',
                                    marginTop: '6px',
                                    marginBottom: '0'
                                }}>
                                    Please enable stock management to set quantity
                                </p>
                            )}
                        </div>
                    </div>
                </ConfirmDialog>

                {/* Premium Modal */}
                <PriceModal
                    isOpen={modalConfig.isOpen}
                    onClose={closeModal}
                    onConfirm={modalConfig.onConfirm}
                    onDecline={closeModal}
                    type={modalConfig.type}
                    title={modalConfig.title}
                    message={modalConfig.message}
                    confirmText={modalConfig.confirmText}
                    declineText={modalConfig.declineText}
                    position={modalConfig.position}
                />

                {/* Batch Progress Modal */}
                <BatchProgressModal
                    isOpen={showBatchModal}
                    onClose={() => {
                        setShowBatchModal(false);
                        resetProgress();
                    }}
                    progress={batchProgress}
                    currentBatch={batchProgress.currentBatch}
                    totalBatches={batchProgress.totalBatches}
                    processedItems={batchProgress.processedItems}
                    totalItems={batchProgress.totalItems}
                    operationType="products"
                    isCompleted={batchProgress.isCompleted}
                    error={batchProgress.error}
                />

            </div>
        </div>
    );
};

// Product View Modal Component
const ProductViewModal = ({ product, onClose }) => {
    if (!product) return null;

    const formatCurrency = (amount, currency = 'USD') => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency
        }).format(amount);
    };

    const [selectedImage, setSelectedImage] = React.useState(0);

    // Prepare gallery images
    const galleryImages = [];
    if (product.images && product.images.length > 0) {
        product.images.forEach(img => {
            if (img.src) {
                galleryImages.push(img.src);
            }
        });
    } else if (product.image) {
        galleryImages.push(product.image);
    }

    const featuredImage = galleryImages[selectedImage] || null;

    // Debug: Log product data
    console.log('Product Data:', {
        name: product.name,
        totalImages: galleryImages.length,
        hasShortDesc: !!product.short_description,
        hasDescription: !!product.description,
        shortDescLength: product.short_description?.length || 0,
        descLength: product.description?.length || 0
    });

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content product-single-page" onClick={(e) => e.stopPropagation()}>
                <button type="button" className="modal-close-btn" onClick={onClose}>
                    ✕
                </button>

                <div className="product-single-container">
                    {/* Left Side - Featured Image & Gallery */}
                    <div className="product-image-section">
                        <div className="featured-image">
                            {featuredImage ? (
                                <img src={featuredImage} alt={product.name} />
                            ) : (
                                <div className="no-image">
                                    <span>📦</span>
                                    <p>No image</p>
                                </div>
                            )}
                        </div>

                        {/* Gallery Thumbnails - Show if more than 1 image */}
                        {galleryImages.length > 1 && (
                            <div className="gallery-thumbnails">
                                {galleryImages.map((img, index) => (
                                    <div
                                        key={index}
                                        className={`thumbnail ${selectedImage === index ? 'active' : ''}`}
                                        onClick={() => setSelectedImage(index)}
                                    >
                                        <img src={img} alt={`${product.name} ${index + 1}`} />
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Descriptions Below Gallery */}
                        {product.short_description && product.short_description.trim() !== '' && (
                            <div className="description-box">
                                <div dangerouslySetInnerHTML={{ __html: product.short_description }} />
                            </div>
                        )}

                        {product.description && product.description.trim() !== '' && (
                            <div className="description-box">
                                <div dangerouslySetInnerHTML={{ __html: product.description }} />
                            </div>
                        )}
                    </div>

                    {/* Right Side - Product Info */}
                    <div className="product-info">
                        {/* Product Title & Meta */}
                        <div className="product-header">
                            <h1>{product.name}</h1>
                            <div className="product-meta">
                                <span className={`status-tag ${product.status}`}>{product.status}</span>
                                {product.featured && <span className="featured-tag">⭐ Featured</span>}
                                <span className="sku-tag">SKU: {product.sku || 'N/A'}</span>
                            </div>
                        </div>

                        {/* Price */}
                        <div className="product-price">
                            {product.sale_price ? (
                                <>
                                    <span className="sale-price">{formatCurrency(product.sale_price)}</span>
                                    <span className="regular-price">{formatCurrency(product.price)}</span>
                                    <span className="discount">
                                        Save {Math.round(((product.price - product.sale_price) / product.price) * 100)}%
                                    </span>
                                </>
                            ) : (
                                <span className="current-price">{product.price ? formatCurrency(product.price) : 'N/A'}</span>
                            )}
                        </div>

                        {/* Stock Status */}
                        <div className="product-stock">
                            <span className={`stock-badge ${product.stock_status}`}>
                                {product.stock_status === 'instock' ? '✓ In Stock' :
                                    product.stock_status === 'outofstock' ? '✗ Out of Stock' :
                                        '⚠ On Backorder'}
                            </span>
                            {product.manage_stock && product.stock_quantity > 0 && (
                                <span className="stock-qty">{product.stock_quantity} units available</span>
                            )}
                        </div>

                        {/* Product Details Table */}
                        <div className="product-details-table">
                            <h3>Product Details</h3>
                            <table>
                                <tbody>
                                    <tr>
                                        <td>Product ID</td>
                                        <td>#{product.id}</td>
                                    </tr>
                                    <tr>
                                        <td>Type</td>
                                        <td>{product.type || 'Simple'}</td>
                                    </tr>
                                    {product.weight && (
                                        <tr>
                                            <td>Weight</td>
                                            <td>{product.weight} kg</td>
                                        </tr>
                                    )}
                                    {product.dimensions && (
                                        <tr>
                                            <td>Dimensions</td>
                                            <td>{product.dimensions.length} × {product.dimensions.width} × {product.dimensions.height} cm</td>
                                        </tr>
                                    )}
                                    <tr>
                                        <td>Date Created</td>
                                        <td>{new Date(product.date_created).toLocaleDateString()}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        {/* Categories & Tags */}
                        {((product.categories && product.categories.length > 0) || (product.tags && product.tags.length > 0)) && (
                            <div className="product-taxonomy">
                                {product.categories && product.categories.length > 0 && (
                                    <div className="tax-group">
                                        <strong>Categories:</strong>
                                        <div className="tax-items">
                                            {product.categories.map((cat, i) => (
                                                <span key={i} className="tax-item category">{cat.name}</span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {product.tags && product.tags.length > 0 && (
                                    <div className="tax-group">
                                        <strong>Tags:</strong>
                                        <div className="tax-items">
                                            {product.tags.map((tag, i) => (
                                                <span key={i} className="tax-item tag">{tag.name}</span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Action Buttons */}
                        <div className="product-actions">
                            <button
                                className="btn btn-primary btn-lg"
                                onClick={() => {
                                    onClose();
                                    window.location.href = `/wp-admin/post.php?post=${product.id}&action=edit`;
                                }}
                            >
                                ✏️ Edit on WooCommerce 
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};



export default Products;